import {
  CompleteObjectInstance,
  CompleteUserInstance,
  FieldInstance,
  UserInstance
} from 'types/interfaces';

const USER_INSTANCE_FIELDS = [
  'ConsoleUserInstanceId',
  'DefaultMessage',
  'DocumentFooter',
  'DocumentHeader',
  'EmailFooter',
  'FromAddress',
  'FromName',
  'Id',
  'IsPublished',
  'ItemOrder',
  'LastModified',
  'LogoURL',
  'NickName',
  'OwnerUserInstanceId',
  'ProfilePicture',
  'ProxyUserInstanceId',
  'RelatedUserPermission',
  'RelationshipStatus',
  'SmtpHost',
  'SmtpPassword',
  'SmtpPort',
  'SmtpSSL',
  'SmtpUsername',
  'SystemAccess',
  'TelephoneNumber',
  'ThirdPartyId',
  'Title',
  'UserDefinitionId',
  'UserInstanceEmail',
  'UserInstancePassword'
] as const;

const OBJECT_INSTANCE_FIELDS = [
  'Id',
  'IsPublished',
  'ItemOrder',
  'LastModified',
  'ObjectDefinitionId',
  'ObjectDescription',
  'ObjectProcessIndependent',
  'ObjectRepeat',
  'ProcessInstanceId',
  'Selected',
  'Title',
  'UserDefinitionId',
  'UserInstanceId'
] as const;

const FIELD_INSTANCE_FIELDS = [
  'FieldDefinitionId',
  'FieldValue',
  'Id',
  'IsPublished',
  'ItemOrder',
  'LastModified',
  'ObjectDefinitionId',
  'ObjectInstanceId',
  'ProcessInstanceId',
  'Title',
  'UserDefinitionId',
  'UserInstanceId'
] as const;

const USER_INSTANCE_INTEGER_FIELDS = [
  'ConsoleUserInstanceId',
  'Id',
  'ItemOrder',
  'OwnerUserInstanceId',
  'ProxyUserInstanceId',
  'RelatedUserPermission',
  'RelationshipStatus',
  'SmtpPort',
  'SystemAccess',
  'UserDefinitionId'
] as const;

const OBJECT_INSTANCE_INTEGER_FIELDS = [
  'Id',
  'ItemOrder',
  'ObjectDefinitionId',
  'ObjectRepeat',
  'ProcessInstanceId',
  'UserDefinitionId',
  'UserInstanceId'
] as const;

const FIELD_INSTANCE_INTEGER_FIELDS = [
  'FieldDefinitionId',
  'Id',
  'ItemOrder',
  'ObjectDefinitionId',
  'ObjectInstanceId',
  'ProcessInstanceId',
  'UserDefinitionId',
  'UserInstanceId'
] as const;

const RELATIONSHIP_FIELDS = [
  'Id',
  'Title',
  'PrimaryUserInstanceId',
  'RelatedUserInstanceId',
  'Status',
  'ProcessInstanceId',
  'AssignedUserDefinitionId',
  'RelatedUserDefinitionId'
] as const;

const RELATIONSHIP_INTEGER_FIELDS = [
  'Id',
  'PrimaryUserInstanceId',
  'RelatedUserInstanceId',
  'Status',
  'ProcessInstanceId',
  'AssignedUserDefinitionId',
  'RelatedUserDefinitionId'
] as const;

const RELATIONSHIP_INPUT_FIELDS = [
  'Title',
  'PrimaryUserInstanceId',
  'RelatedUserInstanceId',
  'Status',
  'RelatedUserPermission'
] as const;

const RELATIONSHIP_INPUT_INTEGER_FIELDS = [
  'PrimaryUserInstanceId',
  'RelatedUserInstanceId',
  'Status'
] as const;

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const pickFields = <T extends readonly string[]>(
  value: unknown,
  fields: T
): Partial<Record<T[number], unknown>> | undefined => {
  if (!isRecord(value)) return undefined;

  return fields.reduce<Partial<Record<T[number], unknown>>>((result, field) => {
    if (Object.prototype.hasOwnProperty.call(value, field)) {
      result[field] = value[field];
    }
    return result;
  }, {});
};

const normalizeInteger = (value: unknown): unknown => {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string' || value.trim() === '') return value;

  const parsedValue = Number(value);
  return Number.isInteger(parsedValue) ? parsedValue : value;
};

const normalizeIntegerFields = (
  value: UnknownRecord,
  fields: readonly string[]
): UnknownRecord => {
  fields.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(value, field)) {
      value[field] = normalizeInteger(value[field]);
    }
  });
  return value;
};

export const normalizeUserInstance = (
  value: unknown
): Partial<UserInstance> | undefined => {
  const userInstance = pickFields(value, USER_INSTANCE_FIELDS);
  if (!userInstance) return undefined;

  return normalizeIntegerFields(
    userInstance,
    USER_INSTANCE_INTEGER_FIELDS
  ) as Partial<UserInstance>;
};

export const normalizeFieldInstance = (
  value: unknown
): Partial<FieldInstance> | undefined => {
  const fieldInstance = pickFields(value, FIELD_INSTANCE_FIELDS);
  if (!fieldInstance) return undefined;

  normalizeIntegerFields(fieldInstance, FIELD_INSTANCE_INTEGER_FIELDS);

  if (
    fieldInstance.FieldValue !== undefined &&
    fieldInstance.FieldValue !== null &&
    typeof fieldInstance.FieldValue !== 'string'
  ) {
    fieldInstance.FieldValue = String(fieldInstance.FieldValue);
  }

  return fieldInstance as Partial<FieldInstance>;
};

export const normalizeCompleteObjectInstance = (
  value: unknown
): Partial<CompleteObjectInstance> | undefined => {
  if (!isRecord(value)) return undefined;

  const objectInstance = pickFields(value.ObjectInstance, OBJECT_INSTANCE_FIELDS);
  if (objectInstance) {
    normalizeIntegerFields(objectInstance, OBJECT_INSTANCE_INTEGER_FIELDS);
  }

  const fieldInstanceList = Array.isArray(value.FieldInstanceList)
    ? value.FieldInstanceList.map(
        (fieldInstance) => normalizeFieldInstance(fieldInstance) ?? {}
      )
    : undefined;

  return {
    ...(objectInstance ? { ObjectInstance: objectInstance } : {}),
    ...(fieldInstanceList ? { FieldInstanceList: fieldInstanceList } : {})
  } as Partial<CompleteObjectInstance>;
};

export const normalizeRelationshipItem = (value: unknown): UnknownRecord => {
  if (!isRecord(value)) return {};

  if (
    Object.prototype.hasOwnProperty.call(value, 'PrimaryUserInstanceId') ||
    Object.prototype.hasOwnProperty.call(value, 'RelatedUserInstanceId')
  ) {
    const relationship = pickFields(value, RELATIONSHIP_FIELDS) ?? {};
    return normalizeIntegerFields(relationship, RELATIONSHIP_INTEGER_FIELDS);
  }

  return (normalizeUserInstance(value) ?? {}) as UnknownRecord;
};

export const normalizeRelationshipInput = (value: unknown): UnknownRecord => {
  const relationship = pickFields(value, RELATIONSHIP_INPUT_FIELDS) ?? {};
  return normalizeIntegerFields(
    relationship,
    RELATIONSHIP_INPUT_INTEGER_FIELDS
  );
};

/**
 * Keeps the direct Cosnetics DETAIL response compatible with the former
 * GraphQL selection and with CompleteUserInstanceInput. Cosnetics can return
 * additional fields which GraphQL rejects when the same object is updated.
 */
export const normalizeCompleteUserInstance = (
  value: unknown
): Partial<CompleteUserInstance> => {
  if (!isRecord(value)) return {};

  const userInstance = normalizeUserInstance(value.UserInstance);
  const completeObjectInstanceList = Array.isArray(
    value.CompleteObjectInstanceList
  )
    ? value.CompleteObjectInstanceList.map(
        (completeObjectInstance) =>
          normalizeCompleteObjectInstance(completeObjectInstance) ?? {}
      )
    : undefined;

  return {
    ...(userInstance ? { UserInstance: userInstance } : {}),
    ...(completeObjectInstanceList
      ? { CompleteObjectInstanceList: completeObjectInstanceList }
      : {})
  } as Partial<CompleteUserInstance>;
};
